import { autocompletion, acceptCompletion } from "@codemirror/autocomplete";
import { keymap } from "@codemirror/view";

/**
 * Smart matching function - checks if query matches label at word boundaries
 * Prioritizes matches at the start of words or exact matches
 * Prevents matches within words (e.g., "top" won't match "Christopher")
 * @param {string} query - The search query
 * @param {string} label - The entity label to match against
 * @returns {Object} Match result with score and match info, or null if no match
 */
function smartMatch(query, label) {
	if (!query || !label) return null;
	
	const normalizedQuery = query.toLowerCase().trim();
	const normalizedLabel = label.toLowerCase();
	
	if (normalizedLabel === normalizedQuery) {
		return { score: 100, type: 'exact' };
	}
	
	if (normalizedLabel.startsWith(normalizedQuery)) {
		return { score: 90, type: 'starts-with' };
	}
	
	const words = normalizedLabel.split(/\s+/);
	for (let i = 0; i < words.length; i++) {
		const word = words[i];
		if (word.startsWith(normalizedQuery)) {
			return { score: 80 - i, type: 'word-start', wordIndex: i };
		}
	}
	
	const escapedQuery = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const wordRegex = new RegExp(`\\b${escapedQuery}\\b`, 'i');
	if (wordRegex.test(normalizedLabel)) {
		return { score: 70, type: 'whole-word' };
	}
	
	if (normalizedQuery.length >= 3) {
		const index = normalizedLabel.indexOf(normalizedQuery);
		if (index !== -1) {
			const beforeChar = index > 0 ? normalizedLabel[index - 1] : ' ';
			const afterChar = index + normalizedQuery.length < normalizedLabel.length 
				? normalizedLabel[index + normalizedQuery.length] 
				: ' ';
			
			const isWordBoundaryBefore = index === 0 || /\s|-/.test(beforeChar);
			const isWordBoundaryAfter = /\s|-|$/.test(afterChar);
			const isNotInMiddleOfWord = !/[a-z0-9]/.test(beforeChar) || index === 0;
			
			if (isWordBoundaryBefore && isWordBoundaryAfter && isNotInMiddleOfWord) {
				return { score: 50 - index, type: 'substring-boundary' };
			}
		}
	}
	
	return null;
}

/**
 * Creates an autocomplete source function for CodeMirror
 * @param {Array<Object>} entities - Array of normalized entities from entityUtils
 * @param {number} maxSuggestions - Maximum number of suggestions to show (default: 10)
 * @param {number} minChars - Minimum characters before showing suggestions (default: 2)
 * @returns {Function} CodeMirror autocomplete source function
 */
export function createAutocompleteSource(entities, maxSuggestions = 5, minChars = 3) {
	if (!entities || entities.length === 0) {
		return () => null;
	}

	const searchEntities = (query) => {
		if (!query || query.trim().length === 0) {
			return [];
		}

		const normalizedQuery = query.toLowerCase().trim();
		
		const matchesWithScores = entities
			.map((entity) => {
				const matchResult = smartMatch(normalizedQuery, entity.label);
				if (!matchResult) return null;
				
				return {
					entity,
					matchResult,
					score: matchResult.score - (entity.label.length / 100),
				};
			})
			.filter((item) => item !== null)
			.sort((a, b) => {
				if (b.score !== a.score) {
					return b.score - a.score;
				}
				if (a.entity.label.length !== b.entity.label.length) {
					return a.entity.label.length - b.entity.label.length;
				}
				return a.entity.label.localeCompare(b.entity.label);
			})
			.slice(0, maxSuggestions)
			.map(({ entity }) => {
				return {
					label: entity.label,
					type: entity.type,
					info: entity.type,
					detail: entity.type,
					apply: entity.value,
				};
			});

		return matchesWithScores;
	};

	return (context) => {
		// Match backwards to capture the current search phrase, including whitespace and dashes
		const fullMatch = context.matchBefore(/\S+(?:[\s-]+\S+){0,9}/);
		
		if (!fullMatch || (fullMatch.from === fullMatch.to && !context.explicit)) {
			return null;
		}

		const fullText = context.state.sliceDoc(fullMatch.from, fullMatch.to);
		
		// Use a regex that captures both words and separators
		const tokens = [];
		const tokenRegex = /(\S+)([\s-]*)/g;
		let match;
		
		while ((match = tokenRegex.exec(fullText)) !== null) {
			tokens.push({
				word: match[1],
				separator: match[2],
				start: fullMatch.from + match.index,
				end: fullMatch.from + match.index + match[0].length
			});
		}
		
		if (tokens.length === 0) {
			return null;
		}

		let bestMatch = null;
		let bestOptions = [];
		let bestRange = null;
		
		for (let wordCount = Math.min(4, tokens.length); wordCount >= 1; wordCount--) {
			const phraseTokens = tokens.slice(-wordCount);
			
			// Reconstruct the query preserving original separators (spaces/dashes)
			const queryParts = [];
			let phraseStart = phraseTokens[0].start;
			let phraseEnd = phraseTokens[phraseTokens.length - 1].end;
			
			for (let i = 0; i < phraseTokens.length; i++) {
				queryParts.push(phraseTokens[i].word);
				
				if (i < phraseTokens.length - 1) {
					const sep = phraseTokens[i].separator;
					if (sep && sep.length > 0) {
						if (sep.includes('-')) {
							queryParts.push(sep);
						} else {
							queryParts.push(' ');
						}
					} else {
						queryParts.push(' ');
					}
				}
			}
			
			const query = queryParts.join('');
			
			if (query.length < minChars && !context.explicit) {
				continue;
			}

			let options = searchEntities(query);
			if (options.length === 0 && query.includes('-')) {
				const normalizedQuery = query.replace(/\s*-\s*/g, ' ').replace(/\s+/g, ' ').trim();
				if (normalizedQuery !== query) {
					options = searchEntities(normalizedQuery);
					if (options.length > 0) {
						bestMatch = query;
						bestOptions = options;
						bestRange = {
							from: phraseStart,
							to: phraseEnd
						};
						break;
					}
				}
			}
			
			if (options.length > 0) {
				bestMatch = query;
				bestOptions = options;
				// Calculate the range: from the start of the first token to the end of the last token
				bestRange = {
					from: phraseStart,
					to: phraseEnd
				};
				break; // Use the longest match that has results
			}
		}
		
		if (!bestMatch || bestOptions.length === 0 || !bestRange) {
			return null;
		}
		
		return {
			from: bestRange.from,
			to: bestRange.to,
			options: bestOptions,
			validFor: /^\S+(?:[\s-]+\S*)*$/, // Allow whitespace and dashes: word, then any number of (whitespace/dash + optional word chars)
			selected: 0,
		};
	};
}

/**
 * Creates CodeMirror autocomplete extension with inline completion and Tab support
 * @param {Array<Object>} entities - Array of normalized entities
 * @param {number} maxSuggestions - Maximum number of suggestions
 * @param {number} minChars - Minimum characters before showing suggestions (default: 2)
 * @returns {Array<Extension>} CodeMirror extensions array
 */
export function createAutocompleteExtension(entities, maxSuggestions = 5, minChars = 3) {
	const source = createAutocompleteSource(entities, maxSuggestions, minChars);
	
	const extensions = [
		autocompletion({
			override: [source],
			activateOnTyping: true,
			maxRenderedOptions: maxSuggestions,
			defaultKeymap: true,
		}),
		keymap.of([
			{
				key: "Tab",
				run: (view) => {
					return acceptCompletion(view);
				},
			},
		]),
	];
	
	return extensions;
}

